import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Clock, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ScheduleOpening {
  open: number;
  close: number;
}

interface ScheduleDay {
  day: number;
  openings: ScheduleOpening[];
}

interface EatzySchedules {
  business?: ScheduleDay[];
  takeout?: ScheduleDay[];
  delivery?: ScheduleDay[];
  catering?: ScheduleDay[];
}

interface LiveStatus {
  open: boolean;
  nextOpenAt?: number;
  nextCloseAt?: number;
  message?: string;
}

interface RestaurantHoursPopoverProps {
  liveStatus?: LiveStatus;
  schedules?: EatzySchedules;
  timezone?: string;
  className?: string;
  /** 
   * Optional override for open/closed status display.
   * Per MENU_ORDERING_STATES.md: orderAvailability.isAvailable (from menu fetch) 
   * is the source of truth for ordering, not liveStatus.open.
   * When provided, this takes precedence over liveStatus.open for display.
   */
  isAvailableOverride?: boolean;
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function secondsToTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const period = hours >= 12 ? 'pm' : 'am';
  const hours12 = hours % 12 || 12;
  return minutes === 0 ? `${hours12}${period}` : `${hours12}:${minutes.toString().padStart(2, '0')}${period}`;
}

function formatTimestamp(timestamp: number, timezone?: string): string {
  const date = new Date(timestamp * 1000);
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: timezone || undefined,
    });
    return formatter.format(date).toLowerCase().replace(' ', '');
  } catch {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const period = hours >= 12 ? 'pm' : 'am';
    const hours12 = hours % 12 || 12;
    return minutes === 0 ? `${hours12}${period}` : `${hours12}:${minutes.toString().padStart(2, '0')}${period}`;
  }
}

function getTodayIndex(timezone?: string): number {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      timeZone: timezone || undefined,
    });
    const dayName = formatter.format(now);
    return DAY_LABELS.indexOf(dayName);
  } catch {
    return new Date().getDay();
  }
}

function getEffectiveSchedule(schedules?: EatzySchedules): ScheduleDay[] | null {
  if (!schedules) return null;
  
  const checkHasOpenings = (schedule?: ScheduleDay[]) => {
    if (!schedule) return false;
    return schedule.some(day => day.openings && day.openings.length > 0);
  };
  
  if (checkHasOpenings(schedules.business)) return schedules.business!;
  if (checkHasOpenings(schedules.takeout)) return schedules.takeout!;
  if (checkHasOpenings(schedules.delivery)) return schedules.delivery!;
  
  return null;
}

/**
 * Convert JavaScript day index (Sunday=0) to Eatzy day index (Monday=0, ISO 8601)
 * JS: Sun=0, Mon=1, Tue=2, Wed=3, Thu=4, Fri=5, Sat=6
 * Eatzy (ISO 8601): Mon=0, Tue=1, Wed=2, Thu=3, Fri=4, Sat=5, Sun=6
 */
function jsToEatzyDayIndex(jsDayIndex: number): number {
  return (jsDayIndex + 6) % 7;
}

function getDayOpenings(schedule: ScheduleDay[], jsDayIndex: number): ScheduleOpening[] {
  // Eatzy API uses ISO 8601: Monday=0, Tuesday=1, ..., Sunday=6
  // JavaScript uses: Sunday=0, Monday=1, ..., Saturday=6
  // Convert JS day index to Eatzy day index
  const eatzyDayIndex = jsToEatzyDayIndex(jsDayIndex);
  
  const dayData = schedule.find(d => d.day === eatzyDayIndex);
  return dayData?.openings || [];
}

export function RestaurantHoursPopover({ 
  liveStatus, 
  schedules, 
  timezone,
  className,
  isAvailableOverride 
}: RestaurantHoursPopoverProps) {
  const hasLiveStatus = liveStatus !== undefined || isAvailableOverride !== undefined;
  const effectiveSchedule = getEffectiveSchedule(schedules);
  const hasScheduleData = effectiveSchedule !== null;
  
  if (!hasLiveStatus && !hasScheduleData) {
    return null;
  }

  // Use isAvailableOverride (from orderAvailability) if provided, otherwise fall back to liveStatus.open
  const isOpen = isAvailableOverride !== undefined ? isAvailableOverride : (liveStatus?.open ?? false);
  const todayIndex = getTodayIndex(timezone);
  const todayOpenings = effectiveSchedule ? getDayOpenings(effectiveSchedule, todayIndex) : [];
  
  const getStatusText = (): string => {
    if (hasLiveStatus) {
      if (isOpen) {
        if (liveStatus?.nextCloseAt) {
          return `Closes ${formatTimestamp(liveStatus.nextCloseAt, timezone)}`;
        }
        if (todayOpenings.length > 0) {
          return `Closes ${secondsToTime(todayOpenings[todayOpenings.length - 1].close)}`;
        }
        return '';
      } else {
        if (liveStatus?.nextOpenAt) {
          return `Opens ${formatTimestamp(liveStatus.nextOpenAt, timezone)}`;
        }
        // Today has openings later (restaurant is between sessions, e.g. after lunch before dinner)
        if (todayOpenings.length > 0) {
          return `Opens ${secondsToTime(todayOpenings[0].open)}`;
        }
        // Today is fully closed — find next day with openings and show day name
        if (effectiveSchedule) {
          for (let offset = 1; offset <= 7; offset++) {
            const nextJsDayIndex = (todayIndex + offset) % 7;
            const nextOpenings = getDayOpenings(effectiveSchedule, nextJsDayIndex);
            if (nextOpenings.length > 0) {
              const dayLabel = offset === 1 ? 'Tomorrow' : DAY_LABELS[nextJsDayIndex];
              return `Opens ${dayLabel} at ${secondsToTime(nextOpenings[0].open)}`;
            }
          }
        }
        return '';
      }
    }
    if (todayOpenings.length > 0) {
      return `${secondsToTime(todayOpenings[0].open)} - ${secondsToTime(todayOpenings[todayOpenings.length - 1].close)}`;
    }
    return '';
  };

  const renderDayHours = (dayIndex: number): string => {
    if (!effectiveSchedule) return 'Closed';
    const openings = getDayOpenings(effectiveSchedule, dayIndex);
    if (openings.length === 0) {
      return 'Closed';
    }
    return openings
      .map(slot => `${secondsToTime(slot.open)} - ${secondsToTime(slot.close)}`)
      .join(', ');
  };

  const statusText = getStatusText();

  const statusDisplay = (
    <div className={cn("flex items-center gap-1.5 text-sm", className)}>
      {hasLiveStatus && (
        <>
          <span className={cn(
            "w-2 h-2 rounded-full",
            isOpen ? "bg-green-500" : "bg-red-500"
          )} />
          <span className={isOpen ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}>
            {isOpen ? 'Open' : 'Closed'}
          </span>
          {statusText && <span className="text-muted-foreground">·</span>}
        </>
      )}
      <Clock className="w-3.5 h-3.5 text-muted-foreground" />
      {statusText && <span className="text-foreground">{statusText}</span>}
      {hasScheduleData && (
        <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
      )}
    </div>
  );

  if (!hasScheduleData) {
    return statusDisplay;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="hover-elevate rounded-md px-1 -mx-1 py-0.5 cursor-pointer transition-colors">
          {statusDisplay}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start">
        <div className="space-y-1.5">
          <h4 className="font-medium text-sm mb-2">Hours</h4>
          {DAY_LABELS.map((label, index) => (
            <div 
              key={index} 
              className={cn(
                "flex justify-between gap-2 text-sm py-0.5",
                index === todayIndex && "font-medium text-primary"
              )}
            >
              <span className={index === todayIndex ? "text-primary" : "text-muted-foreground"}>
                {label}
              </span>
              <span className={index === todayIndex ? "text-primary" : ""}>
                {renderDayHours(index)}
              </span>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
